import {Component, EventEmitter, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faCircleCheck, faTriangleExclamation} from '@fortawesome/pro-solid-svg-icons';
import {BicButtonComponent, BicPopupComponent} from '@buildinginformationcloud/bic-ui-components';
import {RulesetImportResult} from '../../../services/ruleset.service';

/**
 * Ergebnis eines myCheck-Imports (bzw. Fehler eines Ex-/Imports).
 *
 * <p>Bewusst ein Dialog und kein Toast: die Hinweise nennen konkrete Dokumentenklassen und
 * Kontextdateien, die im Ziel nachgezogen werden müssen – das muss lesbar stehen bleiben.
 *
 * <p>Gemeinsam genutzt von der Prüfregelverwaltung und dem Adminbereich. Der Import läuft dort durch
 * dieselbe Backend-Logik; ein zweiter, handgepflegter Dialog würde genau die Hinweise auseinander
 * laufen lassen, auf die es ankommt.
 */
@Component({
  selector: 'app-ruleset-import-feedback',
  standalone: true,
  imports: [FaIconComponent, BicPopupComponent, BicButtonComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <bic-popup (close)="closed.emit()" [hasBackdrop]="true"
               [title]="result ? 'myCheck importiert' : 'Import nicht möglich'"
               customClass="w-[560px]">
      <div bicPopupContent class="flex flex-col gap-4">
        @if (result) {
          <div class="flex flex-row items-start gap-2">
            <fa-icon [icon]="faCircleCheck" class="text-svg-16 text-green shrink-0 mt-0.5"></fa-icon>
            <span class="text-sm text-gray-900">
              „{{ result.name }}" wurde mit
              {{ result.ruleCount }} {{ result.ruleCount === 1 ? 'Prüfregel' : 'Prüfregeln' }}
              {{ targetLabel }}.@if (result.sourceProjectName) {
                <span class="text-gray-500"> Quelle: {{ result.sourceProjectName }}.</span>
              }
            </span>
          </div>

          @if (result.documentClassesCreated?.length) {
            <div class="text-sm text-gray-900">
              Neu angelegte Dokumentenklassen:
              <span class="text-gray-500">{{ result.documentClassesCreated?.join(', ') }}</span>
            </div>
          }

          @if (result.warnings?.length) {
            <div class="flex flex-col gap-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
              <div class="flex flex-row items-center gap-2 text-sm font-medium text-gray-900">
                <fa-icon [icon]="faTriangleExclamation" class="text-svg-14 text-yellow"></fa-icon>
                <span>Bitte nachziehen</span>
              </div>
              <ul class="flex flex-col gap-1 list-disc pl-5">
                @for (warning of result.warnings; track warning) {
                  <li class="text-sm text-gray-900">{{ warning }}</li>
                }
              </ul>
            </div>
          }
        } @else {
          <ul class="flex flex-col gap-1">
            @for (error of errors; track error) {
              <li class="text-sm text-gray-900">{{ error }}</li>
            }
          </ul>
        }
      </div>
      <div bicPopupButtonGroup class="flex flex-row w-full justify-end items-center gap-4">
        <bic-button (onClick)="closed.emit()" buttonType="white">Schließen</bic-button>
        @if (result) {
          <bic-button (onClick)="open.emit()" buttonType="primary">Öffnen</bic-button>
        }
      </div>
    </bic-popup>
  `,
})
export class RulesetImportFeedbackComponent {
  @Input() result: RulesetImportResult | null = null;
  @Input() errors: string[] = [];
  /**
   * Wohin importiert wurde – der einzige inhaltliche Unterschied zwischen Projekt und Adminbereich.
   * Im Adminbereich entsteht ein Entwurf, das muss im Text stehen, sonst wartet der Admin auf ein
   * Regelwerk, das bei den Empfängern nie erscheint.
   */
  @Input() targetLabel = 'in dieses Projekt übernommen';
  @Output() closed = new EventEmitter<void>();
  @Output() open = new EventEmitter<void>();

  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faTriangleExclamation = faTriangleExclamation;
}
